import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { languageService, type Language } from '@/services/admin/languageService';
import { isAdminDomain } from '@/utils/domain';
import api from '@/services/api';

interface LanguageContextType {
    languages: Language[];
    activeLanguages: Language[];
    defaultLanguage: Language | null;
    isLoading: boolean;
    refreshLanguages: () => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
    const [languages, setLanguages] = useState<Language[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchLanguages = async () => {
        try {
            setIsLoading(true);
            let data: Language[];
            if (isAdminDomain()) {
                data = await languageService.getAll();
            } else {
                // Use /languages/available to only expose active+complete languages
                // (same endpoint the header language switcher uses)
                const response = await api.get<{ languages: Language[] } | Language[]>('/languages/available');
                const raw = (response.data as any).languages ?? response.data;
                data = Array.isArray(raw) ? raw : [];
            }
            setLanguages(data);
        } catch (error) {
            console.error('Failed to fetch languages:', error);
            // Fallback to English if API fails
            setLanguages([{
                code: 'en',
                name: 'English',
                native_name: 'English',
                direction: 'ltr',
                is_active: true,
                is_default: true,
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLanguages();
    }, []);

    const activeLanguages = languages.filter(lang => lang.is_active);
    const defaultLanguage = languages.find(lang => lang.is_default) || null;

    const value: LanguageContextType = {
        languages,
        activeLanguages,
        defaultLanguage,
        isLoading,
        refreshLanguages: fetchLanguages,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguageContext(): LanguageContextType {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguageContext must be used within a LanguageProvider');
    }
    return context;
}
